import type { NewsItem as INewsItem } from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import {
  getArchiveYears,
  getLast10,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/newsPages';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts';
import { NewsItem } from '@components/News/NewsItem';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import type { GetStaticProps } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';

export default function NewsLandingPage({
  breadcrumbs,
  newsItems,
  years,
}: {
  breadcrumbs: { title: string; path: string }[];
  newsItems: INewsItem[];
  years: number[];
}): ReactElement {
  return (
    <>
      <Head>
        <title>Latest Bulk Nutrients Flavours, Ambassadors & Products</title>
        <meta
          name="title"
          content="Latest Bulk Nutrients Flavours, Ambassadors & Products"
        />
        <meta
          name="description"
          content="Stay up to date with the latest in new flavours, ambassadors, and products with our Bulk Nutrients News Page. Explore now for your wellness journey."
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/news`}
        />
      </Head>
      <Container>
        <Breadcrumbs crumbs={breadcrumbs} />
      </Container>
      <Container
        as="main"
        id="main-content"
        className="mx-auto my-8 max-w-narrow-page"
      >
        <h1>Bulk Nutrients News</h1>
        <Paragraph>
          The latest Bulk Nutrients flavour releases, discontinued products, new
          ambassadors and more. If it happened at Bulk Nutrients, we've likely
          jotted it down here 👍
        </Paragraph>
        {newsItems.map((item) => (
          <NewsItem
            key={item.title}
            item={item}
            baseUrl="https://www.bulknutrients.com.au/news"
          />
        ))}
        <section className="mt-12.5">
          <h2 className="italic">View the Bulk Nutrients News Archive</h2>
          <div className="flex gap-5">
            {years.sort().map((year) => (
              <TextLink href={`/news/${year}`} key={year}>
                {year}
              </TextLink>
            ))}
          </div>
        </section>
      </Container>
    </>
  );
}

NewsLandingPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const breadcrumbs = [
    { title: 'home', path: '/' },
    { title: 'Bulk Nutrients News', path: '/news' },
  ];

  const newsItems = await getLast10();

  const years = await getArchiveYears();

  return {
    props: {
      breadcrumbs,
      newsItems,
      years,
    },
  };
};
